import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, formatPrice } from '@/lib/utils';
import { Property } from '@/types/property';
import {
  Bath,
  Bed, Briefcase, Building, Construction, Home, LucideIcon, MapPin, Plug
} from 'lucide-react';

export type RoomType = {
  room_id: string;
  property_id: string;
  room_level: number;
  room_type: string;
  room_width: number;
  room_length: number;
  description_1_category: string;
  description_2_category: string;
  description_3_category: string;
  description_1: string;
  description_2: string;
  description_3: string;
  measurement_unit: string;
}

// Core types for structured display
interface DetailItem {
  label: string;
  value: string | number | string[] | null;
  features?: string;
  icon?: LucideIcon;
}

interface DetailSubSection {
  subheading: string;
  details: DetailItem[];
}

interface DetailSection {
  title: string;
  icon: LucideIcon;
  items: DetailSubSection[];
}

// Reusable components for display
export function ArrayValue({ value }: { value: string[] }) {
  if (!value || value.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1 justify-end">
      {value.map((item, i) => (
        <span
          key={i}
          className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

export function DetailItem({ detail }: { detail: DetailItem }) {
  // Skip rendering if value is null/undefined or empty array
  if (detail.value === null || detail.value === undefined || 
     (Array.isArray(detail.value) && detail.value.length === 0)) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{detail.label}</span>
        {Array.isArray(detail.value) ? (
          <ArrayValue value={detail.value} />
        ) : (
          <span className="font-medium">{detail.value}</span>
        )}
      </div>
      {detail.features && (
        <span className="text-sm text-muted-foreground">Features: {detail.features}</span>
      )}
    </div>
  );
}

// Helper function to get level display name
function getLevelName(level: number): string {
  const levelMap: Record<number, string> = {
    0: 'Basement',
    1: 'Main Level',
    2: 'Second Level',
    3: 'Third Level',
    4: 'Fourth Level',
    5: 'Fifth Level',
    6: 'Sixth Level',
  };

  return levelMap[level] || `Level ${level}`;
}

interface PropertyFactsAndFeaturesProps {
  property: Property;
}

export default function PropertyFactsAndFeatures({
  property
}: PropertyFactsAndFeaturesProps) {
  // Define all sections with their icons
  const sectionIcons = {
    'interior': Home,
    'property': Building,
    'construction': Construction,
    'utilities': Plug,
    'location': MapPin,
    'financial': Briefcase
  };

  // Process rooms by level
  const roomsByLevel = property.rooms.reduce<Record<number, RoomType[]>>((acc, room) => {
    if (room.room_level !== null) {
      if (!acc[room.room_level]) {
        acc[room.room_level] = [];
      }
      acc[room.room_level].push(room as RoomType);
    }
    return acc;
  }, {});

  // Transform rooms to detail items
  const roomDetails: DetailSubSection[] = Object.entries(roomsByLevel).map(([level, levelRooms]) => {
    return {
      subheading: getLevelName(Number(level)),
      details: levelRooms.map(room => {
        // Build features from room descriptions
        const features: string[] = [];
        if (room.description_1 && room.description_1_category) {
          features.push(`${room.description_1_category}: ${room.description_1}`);
        }
        if (room.description_2 && room.description_2_category) {
          features.push(`${room.description_2_category}: ${room.description_2}`);
        }
        if (room.description_3 && room.description_3_category) {
          features.push(`${room.description_3_category}: ${room.description_3}`);
        }

        return {
          label: room.room_type,
          value: `${room.room_width} × ${room.room_length} ${room.measurement_unit}`,
          features: features.length > 0 ? features.join(' • ') : undefined
        };
      })
    };
  });

  // Create all detail sections
  const detailSections: DetailSection[] = [
    {
      title: 'Interior',
      icon: sectionIcons.interior,
      items: [
        {
          subheading: 'Bedrooms & bathrooms',
          details: [
            { label: 'Bedrooms', value: property.total_bedrooms || null, icon: Bed },
            { label: 'Bathrooms', value: property.total_bathrooms || null, icon: Bath },
            { label: 'Full bathrooms', value: property.total_bathrooms || null },
            { label: 'Total rooms', value: property.total_rooms || null },
            { label: 'Kitchens', value: property.total_kitchens || null }
          ]
        },
        ...(roomDetails.length > 0 ? roomDetails : []),
        {
          subheading: 'Heating & Cooling',
          details: [
            { label: 'Heating type', value: property.heating_type || null },
            { label: 'Heating source', value: property.heating_source || null },
            { label: 'Cooling type', value: property.cooling_type || null }
          ]
        },
        {
          subheading: 'Interior area',
          details: [
            { label: 'Total square footage', value: property.approx_square_footage ? `${property.approx_square_footage.toLocaleString()} sqft` : null },
          ]
        }
      ]
    },
    {
      title: 'Property',
      icon: sectionIcons.property,
      items: [
        {
          subheading: 'Parking',
          details: [
            { label: 'Garage type', value: property.garage_type || null },
            { label: 'Garage spaces', value: property.garage_spaces || null },
            { label: 'Parking features', value: property.parking_drive_type || null },
            { label: 'Parking spaces', value: property.parking_drive_spaces || null }
          ]
        },
        {
          subheading: 'Lot',
          details: [
            { label: 'Frontage length', value: property.lot_front ? `${property.lot_front} ${property.lot_size_unit}` : null },
            { label: 'Lot depth', value: property.lot_depth ? `${property.lot_depth} ${property.lot_size_unit}` : null },
            { label: 'Lot shape', value: property.lot_shape || null },
            { label: 'Acreage', value: property.acreage ? `${property.acreage} acres` : null },
            { label: 'Is waterfront', value: property.is_waterfront ? 'Yes' : null }
          ]
        },
        {
          subheading: 'Property type',
          details: [
            { label: 'Type', value: property.property_type || null },
            { label: 'Style', value: property.style || null }
          ]
        }
      ]
    },
    {
      title: 'Construction',
      icon: sectionIcons.construction,
      items: [
        {
          subheading: 'Condition',
          details: [
            { label: 'Age', value: property.approx_age ? `${property.approx_age} Years` : null },
            { label: 'Year built', value: property.approx_age ? `${new Date().getFullYear() - property.approx_age}` : null }
          ]
        },
        {
          subheading: 'Materials',
          details: [
            { label: 'Exterior', value: property.exterior || null },
            { label: 'Foundation', value: property.foundation_details || null },
            { label: 'Roof', value: property.roof || null }
          ]
        }
      ]
    },
    {
      title: 'Utilities',
      icon: sectionIcons.utilities,
      items: [
        {
          subheading: 'Utilities',
          details: [
            { label: 'Sewer', value: property.sewer_type || null },
            { label: 'Water', value: property.water_type || null },
            { label: 'Water supply', value: property.water_supply_type || null },
            { label: 'Hydro', value: property.hydro_available || null },
            { label: 'Gas', value: property.gas_available || null },
            { label: 'Alternative power', value: property.alternate_power || null },
            { label: 'Energy certification', value: property.has_energy_cert ? property.energy_cert_level : null }
          ]
        }
      ]
    },
    {
      title: 'Location',
      icon: sectionIcons.location,
      items: [
        {
          subheading: 'Location',
          details: [
            { label: 'Region', value: property.municipality || null },
            { label: 'Community', value: property.community || null },
            { label: 'Area', value: property.area || null },
            { label: 'Postal Code', value: property.postal_code || null }
          ]
        }
      ]
    },
    {
      title: 'Financial',
      icon: sectionIcons.financial,
      items: [
        {
          subheading: 'Listing details',
          details: [
            { label: 'Price', value: property.list_price != null ? formatPrice(property.list_price) : null },
            {
              label: 'Price per square foot',
              value: property.list_price && property.approx_square_footage
                ? `$${Math.round(property.list_price / property.approx_square_footage)}/sqft`
                : null
            },
            { label: 'Annual tax amount', value: property.taxes ? `$${property.taxes.toLocaleString()}` : null },
            { label: 'Tax year', value: property.tax_year || null },
            { label: 'Assessment value', value: property.assessment_value ? `$${property.assessment_value.toLocaleString()}` : null }
          ]
        },
        {
          subheading: 'Inclusions & exclusions',
          details: [
            { label: 'Inclusions', value: property.inclusion_remarks || null },
            { label: 'Exclusions', value: property.exclusion_remarks || null }
          ]
        }
      ]
    }
  ];

  // Filter out empty sections and subsections
  const filteredSections = detailSections
    .map(section => {
      // Filter items to include only those with at least one non-null detail
      const filteredItems = section.items
        .map(item => {
          // Filter details to include only those with non-null values
          const filteredDetails = item.details.filter(detail => 
            detail.value !== null && 
            !(Array.isArray(detail.value) && detail.value.length === 0)
          );

          return {
            ...item,
            details: filteredDetails
          };
        })
        .filter(item => item.details.length > 0);

      return {
        ...section,
        items: filteredItems
      };
    })
    .filter(section => section.items.length > 0);

  // If there are no sections with data, don't render anything
  if (filteredSections.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Facts and Features</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={filteredSections[0]?.title.toLowerCase()} className="w-full">
          <div className="relative">
            <div className="overflow-x-auto pb-2">
              <TabsList className="inline-flex min-w-full gap-2 h-auto">
                {filteredSections.map((section) => (
                  <TabsTrigger
                    key={section.title.toLowerCase()}
                    value={section.title.toLowerCase()}
                    className="flex items-center gap-2 py-2 px-4 whitespace-nowrap"
                  >
                    <section.icon className="h-4 w-4" />
                    <span>{section.title}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-linear-to-r from-background to-transparent pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-linear-to-l from-background to-transparent pointer-events-none" />
          </div>

          {filteredSections.map((section) => (
            <TabsContent 
              key={section.title.toLowerCase()} 
              value={section.title.toLowerCase()} 
              className="space-y-6"
            >
              {section.items.map((item, i) => (
                <div key={i} className="space-y-4">
                  <h3 className="font-semibold text-lg">{item.subheading}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {item.details.map((detail, j) => (
                      <div key={j} className={cn(
                        "col-span-1",
                        item.details.length === 1 && "sm:col-span-2"
                      )}>
                        <DetailItem detail={detail} />
                      </div>
                    ))}
                  </div>
                  {i < section.items.length - 1 && <Separator />}
                </div>
              ))}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}